"use client";

import * as React from "react";
import { useSearchParams } from "next/navigation";
import { Sparkles, Send, Copy, Check, Loader2, Wand2 } from "lucide-react";
import { Button, Textarea, Alert } from "@/components/ui";
import { apiPost, ApiClientError } from "@/lib/api-client";
import { useAuthModal } from "@/features/auth/auth-modal";

const MAX_LEN = 8000;

/** Mẫu gợi ý — bấm để điền sẵn khung yêu cầu, thầy cô chỉnh lại rồi gửi. */
const PRESETS: { label: string; template: string }[] = [
  {
    label: "Soạn câu hỏi trắc nghiệm",
    template:
      "Soạn 5 câu hỏi trắc nghiệm (4 lựa chọn, có đáp án) môn Toán lớp 3, chủ đề phép nhân trong phạm vi 1000. Ghi rõ đáp án đúng.",
  },
  {
    label: "Viết lời nhận xét học sinh",
    template:
      "Viết 3 mẫu lời nhận xét cuối tuần cho học sinh lớp 2: một em học tốt, một em tiến bộ, một em cần cố gắng. Giọng văn ấm áp, khích lệ.",
  },
  {
    label: "Gợi ý hoạt động lớp học",
    template:
      "Gợi ý 5 hoạt động khởi động vui (3–5 phút) cho tiết Tiếng Việt lớp 1, giúp học sinh tập trung.",
  },
  {
    label: "Soạn đề kiểm tra",
    template:
      "Soạn đề kiểm tra 15 phút môn Toán lớp 4 gồm 4 câu (2 trắc nghiệm, 2 tự luận) về phân số, kèm đáp án và thang điểm.",
  },
  {
    label: "Tóm tắt / cải thiện văn bản",
    template: "Giúp tôi viết lại đoạn thông báo sau cho ngắn gọn, trang trọng và dễ hiểu với phụ huynh:\n\n",
  },
];

export function AiAssistant({ isLoggedIn = true }: { isLoggedIn?: boolean }) {
  const searchParams = useSearchParams();
  const { openAuth } = useAuthModal();
  // Prefill khi mở từ "Kho tài nguyên → Prompt AI" (?prompt=...).
  const [prompt, setPrompt] = React.useState(() => (searchParams.get("prompt") ?? "").slice(0, MAX_LEN));
  const [result, setResult] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);
  const taRef = React.useRef<HTMLTextAreaElement>(null);
  // Khách bấm "Gửi" khi chưa đăng nhập → nhớ lại để tự gửi sau khi đăng nhập xong.
  const pendingSubmit = React.useRef(false);

  async function submit() {
    const text = prompt.trim();
    if (!text || loading) return;
    // Chưa đăng nhập → mở modal đăng nhập/đăng ký, chưa gọi API.
    if (!isLoggedIn) {
      pendingSubmit.current = true;
      openAuth("register");
      return;
    }
    setError(null);
    setResult(null);
    setLoading(true);
    try {
      const res = await apiPost<{ text: string }>("/api/ai/generate", { prompt: text });
      setResult(res.text);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Không gọi được trợ lý AI. Thử lại nhé.");
    } finally {
      setLoading(false);
    }
  }

  // Vừa đăng nhập xong (prop đổi false→true) mà đang có yêu cầu chờ → tự gửi.
  React.useEffect(() => {
    if (isLoggedIn && pendingSubmit.current) {
      pendingSubmit.current = false;
      void submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  function usePreset(t: string) {
    setPrompt(t);
    setError(null);
    requestAnimationFrame(() => {
      const el = taRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
    });
  }

  async function copyResult() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* trình duyệt chặn clipboard */
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4 sm:p-6">
      <div className="flex items-start gap-3">
        <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 text-white">
          <Sparkles className="size-6" aria-hidden="true" />
        </span>
        <div>
          <h1 className="text-h3 text-foreground">Trợ lý AI</h1>
          <p className="mt-0.5 text-body-sm text-muted">
            Nhập yêu cầu — soạn câu hỏi, viết nhận xét, gợi ý hoạt động… Trợ lý sẽ giúp bạn ngay.
          </p>
        </div>
      </div>

      {/* Mẫu gợi ý */}
      <div className="flex flex-wrap gap-2">
        {PRESETS.map((p) => (
          <button
            key={p.label}
            type="button"
            onClick={() => usePreset(p.template)}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1.5 text-caption font-medium text-foreground-secondary transition-colors hover:border-primary/50 hover:text-primary"
          >
            <Wand2 className="size-3.5" aria-hidden="true" />
            {p.label}
          </button>
        ))}
      </div>

      {/* Ô nhập */}
      <div className="space-y-2">
        <Textarea
          ref={taRef}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value.slice(0, MAX_LEN))}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "Enter") submit();
          }}
          placeholder="Ví dụ: Soạn 5 câu hỏi trắc nghiệm Toán lớp 3 về phép nhân…"
          rows={5}
        />
        <div className="flex items-center justify-between">
          <span className="text-caption text-muted">
            {prompt.length}/{MAX_LEN} · Ctrl+Enter để gửi
          </span>
          <Button onClick={submit} isLoading={loading} disabled={!prompt.trim()}>
            <Send className="size-4" aria-hidden="true" /> Gửi
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* Kết quả */}
      {loading && (
        <div className="flex items-center justify-center gap-2 rounded-2xl border border-border bg-surface py-12 text-muted">
          <Loader2 className="size-5 animate-spin" aria-hidden="true" />
          Trợ lý đang soạn…
        </div>
      )}

      {result && !loading && (
        <div className="rounded-2xl border border-border bg-surface">
          <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
            <span className="text-caption font-semibold text-muted">Kết quả</span>
            <button
              type="button"
              onClick={copyResult}
              className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-caption font-medium text-primary hover:bg-subtle"
            >
              {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
              {copied ? "Đã chép" : "Chép"}
            </button>
          </div>
          <div className="whitespace-pre-wrap px-4 py-4 text-body-sm leading-relaxed text-foreground">
            {result}
          </div>
        </div>
      )}

      <p className="text-center text-caption text-muted">
        Nội dung do AI tạo, thầy cô nên kiểm tra lại trước khi dùng.
      </p>
    </div>
  );
}
